import React, { useState, useEffect } from 'react';
import { CheckCircle, Clock, Truck, MapPin, MessageSquare, Bell, Mail, Smartphone, Package, ChefHat } from 'lucide-react';
import { Order } from '../api/order';
import RatingModal from './RatingModal';

interface OrderTrackingProps {
  order: Order;
}

interface NotificationPopup {
  id: string;
  type: 'email' | 'sms';
  message: string;
  timestamp: Date;
}

const OrderTracking: React.FC<OrderTrackingProps> = ({ order }) => {
  const [currentStatus, setCurrentStatus] = useState(order.status);
  // const [timeRemaining, setTimeRemaining] = useState(order.estimatedTime * 60);
  const [timeRemaining, setTimeRemaining] = useState(60 * 60);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [deliveryNote, setDeliveryNote] = useState('');
  const [notifications, setNotifications] = useState<NotificationPopup[]>([]);

  // Étapes de suivi avec détails
  const trackingSteps = [
    {
      id: 'confirmed',
      status: 'preparing' as const,
      title: 'Commande confirmée',
      description: 'Votre commande a été reçue et confirmée',
      icon: CheckCircle,
      estimatedTime: '2 min'
    },
    {
      id: 'preparing',
      status: 'PREPARING' as const,
      title: 'Préparation en cours',
      description: 'Nos chefs préparent votre commande avec soin',
      icon: ChefHat,
      estimatedTime: '15-20 min'
    },
    {
      id: 'ready',
      status: 'CREATED' as const,
      title: 'Prêt pour livraison',
      description: 'Votre commande est prête, le livreur va partir',
      icon: Package,
      estimatedTime: '2 min'
    },
    {
      id: 'delivering',
      status: 'DELIVERING' as const,
      title: 'En cours de livraison',
      description: 'Le livreur est en route vers votre adresse',
      icon: Truck,
      estimatedTime: '10-15 min'
    },
    {
      id: 'delivered',
      status: 'DELIVERED' as const,
      title: 'Livré',
      description: 'Votre commande a été livrée avec succès',
      icon: MapPin,
      estimatedTime: 'Terminé'
    }
  ];

  const getCurrentStepIndex = () => {
    return trackingSteps.findIndex(step => step.status === currentStatus);
  };

  const addNotification = (type: 'email' | 'sms', message: string) => {
    const notification: NotificationPopup = {
      id: `notif-${Date.now()}`,
      type,
      message,
      timestamp: new Date()
    };

    setNotifications(prev => [...prev, notification]);

    // Auto-remove notification after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 5000);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Simulate status progression with notifications
    const statusTimer = setTimeout(() => {
      if (currentStatus === 'PREPARING') {
        setCurrentStatus('PREPARING');
        addNotification('email', 'Votre commande est prête pour la livraison !');
        addNotification('sms', 'SMS: Commande prête - Le livreur va partir');

        setTimeout(() => {
          setCurrentStatus('DELIVERING');
          addNotification('email', 'Votre livreur est en route !');
          addNotification('sms', 'SMS: Livreur en route - Arrivée dans 10-15 min');

          setTimeout(() => {
            setCurrentStatus('DELIVERED');
            addNotification('email', 'Commande livrée avec succès !');
            addNotification('sms', 'SMS: Livraison terminée - Bon appétit !');
            // Show rating modal after a short delay
            setTimeout(() => {
              setShowRatingModal(true);
            }, 2000);
          }, 12000);
        }, 8000);
      }
    }, 10000);

    return () => {
      clearInterval(timer);
      clearTimeout(statusTimer);
    };
  }, [currentStatus]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleNoteSubmit = () => {
    if (deliveryNote.trim()) {
      addNotification('email', 'Votre note a été transmise au livreur');
      // Here you would typically send the note to your backend
    }
  };

  const handleRatingSubmit = (rating: number, comment: string) => {
    // Here you would typically send the rating to your backend
    console.log('Rating submitted:', { rating, comment, orderId: order.id });
    addNotification('email', 'Merci pour votre évaluation !');
  };

  const currentStepIndex = getCurrentStepIndex();

  return (
    <div className="max-w-4xl mx-auto relative">
      {/* Notification Popups */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className="bg-white border border-gray-200 rounded-xl shadow-lg p-4 max-w-sm animate-slide-in-right"
          >
            <div className="flex items-start space-x-3">
              <div className={`p-2 rounded-full ${notification.type === 'email'
                ? 'bg-blue-100 text-blue-600'
                : 'bg-green-100 text-green-600'
                }`}>
                {notification.type === 'email' ? (
                  <Mail className="w-4 h-4" />
                ) : (
                  <Smartphone className="w-4 h-4" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {notification.type === 'email' ? 'Email envoyé' : 'SMS envoyé'}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {notification.message}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {notification.timestamp.toLocaleTimeString('fr-FR', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-black mb-2">Suivi de commande</h1>
        <p className="text-gray-600">Commande #{order.id}</p>
      </div>

      {/* Timer */}
      {currentStatus !== 'DELIVERED' && (
        <div className="text-center mb-8">
          <div className="bg-orange-100 rounded-full p-8 w-32 h-32 mx-auto flex items-center justify-center mb-4">
            <span className="text-2xl font-bold text-orange-500">
              {formatTime(timeRemaining)}
            </span>
          </div>
          <p className="text-gray-600">Temps de livraison estimé</p>
        </div>
      )}

      {/* Timeline */}
      <div className="bg-white rounded-xl border border-gray-100 p-8 mb-8">
        <h2 className="text-xl font-semibold text-black mb-6 text-center">
          Progression de votre commande
        </h2>

        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>
          <div
            className="absolute left-8 top-0 w-0.5 bg-orange-500 transition-all duration-1000 ease-out"
            style={{
              height: `${(currentStepIndex / (trackingSteps.length - 1)) * 100}%`
            }}
          ></div>

          <div className="space-y-8">
            {trackingSteps.map((step, index) => {
              const isCompleted = index <= currentStepIndex;
              const isCurrent = index === currentStepIndex;
              const IconComponent = step.icon;

              return (
                <div key={step.id} className="relative flex items-center">
                  {/* Step Icon */}
                  <div className={`relative z-10 w-16 h-16 rounded-full flex items-center justify-center border-4 transition-all duration-500 ${isCompleted
                    ? 'border-orange-500 bg-orange-500 shadow-lg shadow-orange-200'
                    : 'border-gray-200 bg-white'
                    }`}>
                    <IconComponent className={`w-6 h-6 transition-colors duration-500 ${isCompleted ? 'text-white' : 'text-gray-400'
                      }`} />
                  </div>

                  {/* Step Content */}
                  <div className="ml-6 flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className={`font-semibold text-lg transition-colors duration-500 ${isCompleted ? 'text-black' : 'text-gray-400'
                        }`}>
                        {step.title}
                      </h3>
                      <span className={`text-sm px-3 py-1 rounded-full transition-all duration-500 ${isCompleted
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-gray-100 text-gray-500'
                        }`}>
                        {step.estimatedTime}
                      </span>
                    </div>

                    <p className={`text-sm transition-colors duration-500 ${isCompleted ? 'text-gray-600' : 'text-gray-400'
                      }`}>
                      {step.description}
                    </p>

                    {isCurrent && currentStatus !== 'DELIVERED' && (
                      <div className="mt-3">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                          <span className="text-sm text-orange-600 font-medium">
                            En cours...
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Delivery Note */}
      {currentStatus === 'DELIVERING' && (
        <div className="bg-white rounded-xl border border-gray-100 p-6 mb-8">
          <h3 className="text-lg font-semibold text-black mb-4 flex items-center">
            <MessageSquare className="w-5 h-5 mr-2 text-orange-500" />
            Note pour le livreur
          </h3>

          <div className="space-y-4">
            <textarea
              value={deliveryNote}
              onChange={(e) => setDeliveryNote(e.target.value)}
              placeholder="Instructions spéciales pour la livraison (étage, code d'accès, etc.)"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
              rows={3}
            />

            <button
              onClick={handleNoteSubmit}
              disabled={!deliveryNote.trim()}
              className={`px-6 py-2 rounded-lg font-medium transition-colors duration-200 ${deliveryNote.trim()
                ? 'bg-orange-500 text-white hover:bg-orange-600'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
            >
              Envoyer la note
            </button>
          </div>
        </div>
      )}

      {/* Order Details */}
      <div className="bg-white rounded-xl border border-gray-100 p-6 mb-8">
        <h3 className="text-lg font-semibold text-black mb-4">Détails de la commande</h3>

        <div className="space-y-3 mb-4">
          {order.items.map(item => (
            <div key={item.dish.id} className="flex items-center space-x-4">
              <img
                src={item.dish.imageUrl}
                alt={item.dish.name}
                className="w-16 h-16 object-cover rounded-lg"
              />
              <div className="flex-1">
                <h4 className="font-medium text-black">{item.dish.name}</h4>
                <p className="text-sm text-gray-600">Quantité: {item.quantity}</p>
              </div>
              <span className="font-semibold text-orange-500">
                ${(item.dish.price * item.quantity).toFixed(2)} CAD
              </span>
            </div>
          ))}
        </div>

        <div className="border-t border-gray-200 pt-4">
          <div className="flex justify-between font-bold text-lg">
            <span>Total</span>
            <span className="text-orange-500">${order.totalPrice.toFixed(2)} CAD</span>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
          <p className="text-sm text-gray-600">
            <strong>Livraison:</strong> {order.address.fullAddress}
          </p>
          {/* <p className="text-sm text-gray-600">
            <strong>Téléphone:</strong> {order.address.phone}
          </p>
          <p className="text-sm text-gray-600">
            <strong>Client:</strong> {order.address.name}
          </p> */}
        </div>
      </div>

      {/* Rating Modal */}
      <RatingModal
        isOpen={showRatingModal}
        onClose={() => setShowRatingModal(false)}
        orderNumber={order.id}
        onSubmitRating={handleRatingSubmit}
      />

      {/* <style jsx>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style> */}
    </div>
  );
};

export default OrderTracking;